// ─── Color Palette ───────────────────────────────────────────────────────────
const PALETTE = {
  primary:     { base: "#2e7cba", light: "#5da3da", dark: "#1a5b8e", glow: "rgba(46,124,186,0.18)" },
  blue:        { base: "#3b82f6", light: "#60a5fa", dark: "#2563eb", glow: "rgba(59,130,246,0.15)" },
  amber:       { base: "#f59e0b", light: "#fbbf24", dark: "#d97706", glow: "rgba(245,158,11,0.15)" },
  emerald:     { base: "#10b981", light: "#34d399", dark: "#059669", glow: "rgba(16,185,129,0.15)" },
  violet:      { base: "#8b5cf6", light: "#a78bfa", dark: "#7c3aed", glow: "rgba(139,92,246,0.15)" },
  cyan:        { base: "#06b6d4", light: "#22d3ee", dark: "#0891b2", glow: "rgba(6,182,212,0.15)" },
  rose:        { base: "#f43f5e", light: "#fb7185", dark: "#e11d48", glow: "rgba(244,63,94,0.15)" },
};

// ─── Shared Tokens ───────────────────────────────────────────────────────────
const radius = {
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "20px",
  pill: "9999px",
};

const shadow = (theme) => ({
  sm:   theme === "dark" ? "0 2px 8px rgba(0,0,0,0.3)"   : "0 2px 8px rgba(0,0,0,0.04)",
  md:   theme === "dark" ? "0 4px 16px rgba(0,0,0,0.35)"  : "0 4px 16px rgba(0,0,0,0.06)",
  lg:   theme === "dark" ? "0 8px 32px rgba(0,0,0,0.4)"   : "0 8px 32px rgba(0,0,0,0.08)",
  glow: (color) => `0 0 0 1px ${color}22, 0 4px 20px ${color}18`,
});

const bg = (theme) => ({
  page:    theme === "dark" ? "#0f172a"                  : "#f8fafc",
  card:    theme === "dark" ? "rgba(30,41,59,0.6)"       : "rgba(255,255,255,0.95)",
  cardAlt: theme === "dark" ? "rgba(51,65,85,0.35)"      : "rgba(248,250,252,0.9)",
  input:   theme === "dark" ? "rgba(30,41,59,0.8)"       : "#ffffff",
  hover:   theme === "dark" ? "rgba(255,255,255,0.04)"   : "rgba(0,0,0,0.02)",
  border:  theme === "dark" ? "rgba(148,163,184,0.12)"   : "rgba(226,232,240,0.8)",
});

const text = (theme) => ({
  primary:   theme === "dark" ? "#f1f5f9" : "#0f172a",
  secondary: theme === "dark" ? "rgba(226,232,240,0.7)" : "#64748b",
  muted:     theme === "dark" ? "rgba(148,163,184,0.6)" : "#94a3b8",
});


// =============================================================================
export const getStyles = (theme, isRTL) => {
  const s = shadow(theme);
  const b = bg(theme);
  const t = text(theme);

  return {
    // ─── Page Container ────────────────────────────────────────────────────
    container: {
      marginTop: -40,
    },

    // ─── Header ────────────────────────────────────────────────────────────
    header: {
      background: theme === "dark"
        ? `linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(30,41,59,0.95) 50%, rgba(139,92,246,0.06) 100%)`
        : `linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(255,255,255,0.98) 50%, rgba(139,92,246,0.03) 100%)`,
      padding: "clamp(28px, 4.5vw, 44px)",
      borderRadius: radius.xl,
      marginBottom: "24px",
      border: `1px solid ${b.border}`,
      position: "relative",
      overflow: "hidden",
    },

    headerGradientOverlay: {
      position: "absolute",
      top: 0,
      [isRTL ? "right" : "left"]: 0,
      width: "100%",
      height: "100%",
      background: theme === "dark"
        ? `radial-gradient(ellipse at ${isRTL ? "right" : "left"} top, rgba(99,102,241,0.08) 0%, transparent 60%)`
        : `radial-gradient(ellipse at ${isRTL ? "right" : "left"} top, rgba(99,102,241,0.05) 0%, transparent 60%)`,
      pointerEvents: "none",
    },

    headerContent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "20px",
      position: "relative",
      zIndex: 1,
    },

    headerLeft: {
      flex: 1,
      minWidth: "280px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },

    headerTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      gap: "20px",
      flexWrap: "wrap",
    },

    headerRight: {
      display: "flex",
      flexDirection: "row",
      gap: "12px",
      alignItems: "center",
      flexShrink: 0,
    },

    // ─── Stats ─────────────────────────────────────────────────────────────
    statsGrid: {
      display: "flex",
      gap: "14px",
      marginTop: "18px",
      alignItems: "stretch",
      flexWrap: "wrap",
    },

    statCard: {
      flex: "1 1 180px",
      minWidth: "160px",
      background: b.card,
      borderRadius: radius.md,
      padding: "14px 18px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      border: `1px solid ${b.border}`,
      boxShadow: s.sm,
      transition: "all 0.3s ease",
    },

    statIconWrapper: {
      width: "44px",
      height: "44px",
      borderRadius: radius.md,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      flexShrink: 0,
    },

    statValue: {
      fontSize: "22px",
      fontWeight: "800",
      color: t.primary,
      letterSpacing: "-0.02em",
    },

    statLabel: {
      fontSize: "12px",
      color: t.secondary,
      fontWeight: "600",
    },

    // ─── Typography ────────────────────────────────────────────────────────
    title: {
      fontSize: "clamp(24px, 4vw, 32px)",
      fontWeight: "800",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "clamp(12px, 1.8vw, 16px)",
      color: t.primary,
      flexDirection: "row",
      letterSpacing: "-0.03em",
    },

    subtitle: {
      fontSize: "clamp(13px, 2vw, 15px)",
      color: t.secondary,
      margin: 0,
      textAlign: isRTL ? "right" : "left",
      lineHeight: "1.7",
      fontWeight: "500",
      marginBottom: "16px",
    },

    companyBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 18px",
      borderRadius: radius.md,
      background: PALETTE.primary.glow,
      border: `1px solid ${PALETTE.primary.base}33`,
      color: theme === "dark" ? PALETTE.primary.light : PALETTE.primary.base,
      fontSize: "clamp(13px, 1.8vw, 15px)",
      fontWeight: "700",
    },

    // ─── Detect Button ─────────────────────────────────────────────────────
    detectButton: {
      padding: "12px 24px",
      borderRadius: radius.md,
      border: "none",
      background: `linear-gradient(135deg, ${PALETTE.primary.base} 0%, ${PALETTE.primary.dark} 100%)`,
      color: "white",
      fontSize: "14px",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      whiteSpace: "nowrap",
      position: "relative",
      overflow: "hidden",
      boxShadow: `0 4px 14px ${PALETTE.primary.base}30`,
      letterSpacing: "0.3px",
    },

    // ─── Modal ─────────────────────────────────────────────────────────────
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme === "dark" ? "rgba(0,0,0,0.6)" : "rgba(15,23,42,0.3)",
      backdropFilter: "blur(12px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "24px",
      animation: "ccFadeIn 0.2s ease",
    },

    modal: {
      background: theme === "dark" ? "#1e293b" : "#ffffff",
      borderRadius: radius.xl,
      padding: "clamp(28px, 4.5vw, 40px)",
      maxWidth: "900px",
      width: "100%",
      maxHeight: "85vh",
      overflow: "auto",
      boxShadow: s.lg,
      border: `1px solid ${b.border}`,
      animation: "ccSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      position: "relative",
    },

    modalTitle: {
      fontSize: "clamp(20px, 3.5vw, 28px)",
      fontWeight: "800",
      color: t.primary,
      marginBottom: "8px",
      letterSpacing: "-0.03em",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      flexDirection: isRTL ? "row" : "row",
    },

    modalSubtitle: {
      fontSize: "clamp(13px, 2vw, 15px)",
      color: t.secondary,
      marginBottom: "28px",
      fontWeight: "500",
      textAlign: isRTL ? "right" : "left",
    },

    companyCard: (hasConfig) => ({
      background: hasConfig
        ? theme === "dark" ? "rgba(16,185,129,0.06)" : "#f0fdf4"
        : b.card,
      borderRadius: radius.lg,
      padding: "18px",
      border: hasConfig
        ? `2px solid ${PALETTE.emerald.base}60`
        : `1px solid ${b.border}`,
      transition: "all 0.3s ease",
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minHeight: "200px",
      boxShadow: s.sm,
    }),

    companyName: {
      fontSize: "15px",
      fontWeight: "700",
      color: t.primary,
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      letterSpacing: "-0.01em",
      flexDirection: "row",
      lineHeight: "1.4",
      wordBreak: "break-word",
    },

    companyDetails: {
      display: "flex",
      gap: "12px",
      fontSize: "12px",
      color: t.secondary,
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: "12px",
      flexDirection: "row",
    },

    badge: (configured) => ({
      padding: "4px 12px",
      borderRadius: radius.sm,
      fontSize: "11px",
      fontWeight: "700",
      background: configured
        ? `${PALETTE.emerald.base}18`
        : theme === "dark" ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.1)",
      color: configured
        ? PALETTE.emerald.base
        : t.muted,
      letterSpacing: "0.5px",
      whiteSpace: "nowrap",
      border: configured
        ? `1px solid ${PALETTE.emerald.base}30`
        : `1px solid transparent`,
    }),

    selectCompanyButton: {
      width: "100%",
      padding: "10px 14px",
      borderRadius: radius.md,
      border: "none",
      background: `linear-gradient(135deg, ${PALETTE.primary.base}, ${PALETTE.primary.dark})`,
      color: "white",
      fontSize: "12px",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
      letterSpacing: "0.3px",
      boxShadow: `0 2px 8px ${PALETTE.primary.base}25`,
    },

    autoCreateButton: {
      width: "100%",
      padding: "clamp(14px, 2.5vw, 18px)",
      borderRadius: radius.md,
      border: "none",
      background: `linear-gradient(135deg, ${PALETTE.primary.base}, ${PALETTE.violet.base})`,
      color: "white",
      fontSize: "clamp(14px, 2vw, 16px)",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
      letterSpacing: "0.3px",
      boxShadow: `0 4px 14px ${PALETTE.primary.base}25`,
    },

    closeButton: {
      position: "absolute",
      top: "20px",
      [isRTL ? "left" : "right"]: "20px",
      background: "none",
      border: "none",
      color: t.muted,
      cursor: "pointer",
      fontSize: "28px",
      padding: "0",
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      borderRadius: "50%",
    },

    // ─── Tab Bar ───────────────────────────────────────────────────────────
    tabsContainer: {
      display: "flex",
      gap: "4px",
      marginBottom: "0",
      overflowX: "auto",
      overflowY: "hidden",
      paddingBottom: "12px",
      paddingTop: "8px",
      flexDirection: isRTL ? "row" : "row",
      borderBottom: `2px solid ${b.border}`,
      minHeight: "64px",
    },

    tab: (isActive) => ({
      padding: "12px 20px",
      borderRadius: `${radius.sm} ${radius.sm} 0 0`,
      border: "none",
      borderBottom: isActive
        ? `3px solid ${PALETTE.primary.base}`
        : "3px solid transparent",
      background: isActive
        ? theme === "dark" ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)"
        : "transparent",
      color: isActive
        ? theme === "dark" ? PALETTE.primary.light : PALETTE.primary.base
        : t.secondary,
      fontSize: "14px",
      fontWeight: isActive ? "700" : "600",
      cursor: "pointer",
      transition: "all 0.25s ease",
      whiteSpace: "nowrap",
      letterSpacing: "0.2px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? "row" : "row",
      marginBottom: "0",
      height: "48px",
    }),

    // ─── Content Section (Card) ────────────────────────────────────────────
    contentSection: {
      background: b.card,
      borderRadius: radius.lg,
      padding: "clamp(24px, 4vw, 32px)",
      marginBottom: "20px",
      border: `1px solid ${b.border}`,
      backdropFilter: "blur(12px)",
      boxShadow: s.sm,
    },

    // ─── Section Title ─────────────────────────────────────────────────────
    sectionTitle: {
      fontSize: "clamp(17px, 3vw, 22px)",
      fontWeight: "800",
      color: t.primary,
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      letterSpacing: "-0.02em",
      flexDirection: isRTL ? "row" : "row",
      paddingBottom: "16px",
      borderBottom: `1px solid ${b.border}`,
    },

    // ─── Section Icon Badge ────────────────────────────────────────────────
    sectionIconBadge: (color = PALETTE.primary.base) => ({
      width: "38px",
      height: "38px",
      borderRadius: radius.md,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: `${color}15`,
      color: color,
      flexShrink: 0,
    }),

    // ─── Form Layout ───────────────────────────────────────────────────────
    formRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: "clamp(16px, 3vw, 24px)",
      marginBottom: "20px",
    },

    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },

    label: {
      fontSize: "13px",
      fontWeight: "700",
      color: t.primary,
      letterSpacing: "0.2px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? "row" : "row",
    },

    // ─── Inputs ────────────────────────────────────────────────────────────
    selectOption: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: radius.md,
      border: `1.5px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.9)"}`,
      background: b.input,
      color: t.primary,
      fontSize: "14px",
      fontFamily: "inherit",
      cursor: "pointer",
      transition: "all 0.25s ease",
      direction: isRTL ? "rtl" : "ltr",
      boxSizing: "border-box",
    },

    selectOptionItem: {
      background: theme === "dark" ? "rgba(30,41,59,0.98)" : "rgba(255,255,255,0.98)",
      color: t.primary,
      padding: "12px",
    },

    input: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: radius.md,
      background: b.input,
      border: `1.5px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.9)"}`,
      color: t.primary,
      fontSize: "14px",
      fontFamily: "inherit",
      transition: "all 0.25s ease",
      boxSizing: "border-box",
      boxShadow: `inset 0 1px 2px ${theme === "dark" ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.03)"}`,
    },

    select: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: radius.md,
      border: `1.5px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.9)"}`,
      background: b.input,
      color: t.primary,
      fontSize: "14px",
      fontFamily: "inherit",
      cursor: "pointer",
      transition: "all 0.25s ease",
      direction: isRTL ? "rtl" : "ltr",
      boxSizing: "border-box",
    },

    checkbox: {
      width: "20px",
      height: "20px",
      cursor: "pointer",
      accentColor: PALETTE.primary.base,
    },

    // ─── Toggle Container ──────────────────────────────────────────────────
    toggleContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      borderRadius: radius.md,
      background: b.cardAlt,
      border: `1px solid ${b.border}`,
      marginBottom: "12px",
      transition: "all 0.25s ease",
      flexDirection: "row",
    },

    // ─── Action Buttons ────────────────────────────────────────────────────
    actionButtons: {
      display: "flex",
      gap: "12px",
      marginTop: "24px",
      flexWrap: "wrap",
      justifyContent: "flex-end",
      flexDirection: "row",
    },

    saveButton: {
      padding: "14px 32px",
      borderRadius: radius.md,
      border: "none",
      background: `linear-gradient(135deg, ${PALETTE.primary.base} 0%, ${PALETTE.primary.dark} 100%)`,
      color: "white",
      fontSize: "15px",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      letterSpacing: "0.3px",
      flexDirection: isRTL ? "row" : "row",
      boxShadow: `0 4px 14px ${PALETTE.primary.base}30`,
    },

    resetButton: {
      padding: "14px 28px",
      borderRadius: radius.md,
      border: `1.5px solid ${theme === "dark" ? "rgba(148,163,184,0.25)" : "rgba(226,232,240,0.9)"}`,
      background: "transparent",
      color: t.secondary,
      fontSize: "15px",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      transition: "all 0.3s ease",
      letterSpacing: "0.3px",
      flexDirection: isRTL ? "row" : "row",
    },

    // ─── Success Notification ──────────────────────────────────────────────
    successNotification: {
      position: "fixed",
      bottom: "32px",
      [isRTL ? "left" : "right"]: "32px",
      background: `linear-gradient(135deg, ${PALETTE.emerald.base}, ${PALETTE.emerald.dark})`,
      color: "white",
      padding: "18px 28px",
      borderRadius: radius.lg,
      boxShadow: `0 12px 40px ${PALETTE.emerald.base}35`,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: "15px",
      fontWeight: "700",
      zIndex: 10000,
      animation: "ccSlideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      border: "1px solid rgba(255,255,255,0.2)",
      flexDirection: isRTL ? "row" : "row",
    },

    // ─── Rule Containers ───────────────────────────────────────────────────
    leaveRuleContainer: {
      borderRadius: radius.md,
      padding: "clamp(16px, 2.5vw, 22px)",
      marginBottom: "16px",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: "clamp(16px, 2.5vw, 24px)",
      background: theme === "dark" ? "rgba(255,255,255,0.02)" : "#f8fafc",
      border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
      position: "relative",
      borderLeft: `4px solid ${PALETTE.primary.base}`,
      transition: "all 0.2s ease",
    },

    // ─── Add / Remove Buttons ──────────────────────────────────────────────
    addButton: {
      padding: "10px 18px",
      borderRadius: radius.md,
      border: "none",
      background: `linear-gradient(135deg, ${PALETTE.emerald.base} 0%, ${PALETTE.emerald.dark} 100%)`,
      color: "white",
      fontSize: "14px",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      letterSpacing: "0.3px",
      flexDirection: isRTL ? "row" : "row",
      boxShadow: `0 4px 12px ${PALETTE.emerald.base}25`,
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 6px 16px ${PALETTE.emerald.base}35`,
      },
    },

    removeButton: {
      padding: "10px 18px",
      borderRadius: radius.md,
      border: "none",
      background: `linear-gradient(135deg, ${PALETTE.rose.base} 0%, ${PALETTE.rose.dark} 100%)`,
      color: "white",
      fontSize: "14px",
      fontWeight: "700",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      letterSpacing: "0.3px",
      flexDirection: isRTL ? "row" : "row",
      boxShadow: `0 4px 12px ${PALETTE.rose.base}25`,
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 6px 16px ${PALETTE.rose.base}35`,
      },
    },
  };
};

export { PALETTE }